import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { MicIcon } from './icons.js';
import { headOf, hms, useNow, ymd } from './media.js';

const EASE = [0.16, 1, 0.3, 1];

/**
 * The on-screen display a security feed carries: name and place, a
 * clock that ticks, the LIVE badge and a pulsing REC, over a vignette
 * with faint scanlines and grain.
 *
 * The texture is there so a photograph reads as a camera, and kept
 * quiet enough that it never reads as a gimmick. `variant="soft"` is
 * the residents' version: the clock and LIVE stay, the recorder's
 * vocabulary (REC, camera ids, scanlines) goes.
 *
 * Decorative layers are aria-hidden; the words are left readable.
 */
export function CctvOverlay({ camera, status = 'live', playback = null, variant = 'full', talking = false, top = null }) {
    const soft = variant === 'soft';
    const recording = status === 'live';

    return (
        <div className={`cctv${soft ? ' cctv--soft' : ''}`}>
            <span className="cctv__vignette" aria-hidden="true" />
            {!soft && <span className="cctv__lines" aria-hidden="true" />}
            {!soft && <span className="cctv__roll" aria-hidden="true" />}
            <span className="cctv__grain" aria-hidden="true" />

            <div className="cctv__top">
                <div className="cctv__badges">
                    <StatusBadge status={status} playback={playback} />
                    {/* Night vision, as a real recorder labels a camera
                        that has switched to its infrared illuminator. */}
                    {camera.ir && !soft && status !== 'offline' && <span className="cctv__badge cctv__badge--ir">IR</span>}
                    {talking && (
                        <span className="cctv__badge cctv__badge--talk">
                            <MicIcon aria-hidden="true" /> Talk
                        </span>
                    )}
                </div>
                {top}
            </div>

            <div className="cctv__bottom">
                <div className="cctv__name">
                    <strong>{camera.name}</strong>
                    <span>
                        {soft ? camera.location : `${camera.id.toUpperCase()} · ${camera.location}`}
                    </span>
                </div>
                <div className="cctv__stamp">
                    {!soft && recording && <span className="cctv__rec">REC</span>}
                    <Stamp playback={playback} soft={soft} />
                </div>
            </div>
        </div>
    );
}

function StatusBadge({ status, playback }) {
    if (status === 'offline' || status === 'gap') {
        return <span className="cctv__badge cctv__badge--offline">{status === 'gap' ? 'No recording' : 'Offline'}</span>;
    }
    if (status === 'reconnecting') return <span className="cctv__badge cctv__badge--warn">Connecting</span>;
    if (playback) {
        return (
            <span className="cctv__badge cctv__badge--playback">
                {playback.paused ? 'Paused' : 'Playback'}
            </span>
        );
    }
    return <span className="cctv__badge cctv__badge--live">Live</span>;
}

/* The frame's timestamp. Its own component, so the once-a-second tick
   re-renders a line of text and not the tile around it. */
function Stamp({ playback, soft }) {
    const now = useNow();
    const at = headOf(playback, now);

    return (
        <time className="cctv__time" dateTime={new Date(at).toISOString()}>
            {!soft && <span className="cctv__date">{ymd(at)}</span>}
            {hms(at)}
        </time>
    );
}

/**
 * A motion-detection box: an accent outline with corner ticks and a
 * label, drifting a little as if it were tracking, then fading out.
 * `motion` is { id, x, y, w, h, dx, dy, label } in percent of the frame.
 */
export function MotionBox({ motion: box }) {
    const reduced = useReducedMotion();

    return (
        <AnimatePresence>
            {box && (
                <motion.span
                    key={box.id}
                    className="cam-motion"
                    aria-hidden="true"
                    style={{ left: `${box.x}%`, top: `${box.y}%`, width: `${box.w}%`, height: `${box.h}%` }}
                    initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 1.25 }}
                    animate={reduced
                        ? { opacity: 1 }
                        : { opacity: 1, scale: 1, x: [`0%`, `${box.dx}%`], y: [`0%`, `${box.dy}%`] }}
                    exit={{ opacity: 0, transition: { duration: 0.6 } }}
                    transition={{
                        opacity: { duration: 0.25 },
                        scale: { duration: 0.45, ease: EASE },
                        x: { duration: 3.2, ease: 'easeInOut' },
                        y: { duration: 3.2, ease: 'easeInOut' }
                    }}
                >
                    <span className="cam-motion__label">{box.label}</span>
                </motion.span>
            )}
        </AnimatePresence>
    );
}
